import { useState } from "preact/hooks"
import { Button } from "@/components/ui/button.tsx"
import { toast } from "@/components/ui/use-toast.ts"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip.tsx"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { api_fetch } from "@/api"
import { RefreshFlag } from "@/refresh_flag.tsx"
import { Package, Truck, Star, ExternalLink } from "lucide-react"
import { Deduction } from "@/api"
import { LogisticsProviderForm, LogisticsProvider } from "@/components/logistics_provider_form.tsx"
import { UserState } from "@/auth/user"
import { entity, persistence } from "simpler-state"
import { stord_po_number_to_search } from "./stord_bol_request"

interface BolRequestButtonsProps {
  deduction: Deduction & { has_stord_config: boolean }
  logistics_providers?: LogisticsProvider[]
  guidance_url?: string | null
}

export function BolRequestButtons({ deduction, logistics_providers: all_providers = [], guidance_url }: BolRequestButtonsProps) {
  // An exit-pass contact has its own "Request exit pass" button on the deduction; it is not a
  // BOL/POD contact and never shows here.
  const logistics_providers = all_providers.filter((p: LogisticsProvider) => p.documents !== "exit_pass")
  const [loading, setLoading] = useState(false)
  const [tooltipOpen, setTooltipOpen] = useState(false)
  const [showLogisticsDialog, setShowLogisticsDialog] = useState(false)
  const [showProviderSelection, setShowProviderSelection] = useState(false)
  const [requestKind, setRequestKind] = useState<null | "stord" | "logistics">(null)
  const [poInput, setPoInput] = useState<string>(deduction.po_number ?? "")
  const [selectedProviders, setSelectedProviders] = useState<LogisticsProvider[]>([])
  const [showInlineProviderCreate, setShowInlineProviderCreate] = useState(false)
  const user = UserState.use(u => u)

  // Persisted per-user preferences map: { [userKey]: boolean }
  // true/undefined -> show warning; false -> hide warning
  const WarnPreferenceState = entity<Record<string, boolean>>({}, [persistence("bol_pod_warn_po")])
  const warnPrefs = WarnPreferenceState.use()
  const userKey = ((user as any)?.id || (user as any)?.email || "anon") as string
  function shouldShowWarning(): boolean {
    return warnPrefs[userKey] !== false
  }
  function setDontRemind(value: boolean) {
    WarnPreferenceState.set(prev => ({ ...prev, [userKey]: !value }))
  }

  async function handleStordBolRequest(po_number?: string) {
    setLoading(true)
    try {
      const response = await api_fetch(`/deduction/${deduction.id}/request-stord-bol`, {
        method: "POST",
        body: po_number && po_number.trim().length > 0 ? { po_number } : {},
      })

      if (!response.ok) {
        throw new Error(response.error?.message || "Failed to request BOL from Stord")
      }

      const data = response.value.data as { message: string; files_added: number }

      toast({
        title: "BOL Request Complete",
        description: data.message,
        variant: data.files_added > 0 ? "default" : "destructive",
      })

      // Refresh the deduction data
      RefreshFlag.set(f => !f)
    } catch (error) {
      console.error("Error requesting BOL from Stord:", error)
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to request BOL from Stord",
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }

  async function handleLogisticsProviderRequest() {
    // Check if logistics providers are configured
    if (logistics_providers.length === 0) {
      setShowLogisticsDialog(true)
      return
    }

    // If only one provider, open modal with it preselected; else open with defaults
    setSelectedProviders(
      logistics_providers.length === 1
        ? [logistics_providers[0]]
        : logistics_providers.length <= 5
          ? logistics_providers
          : []
    )
    setRequestKind("logistics")
  }

  async function sendLogisticsRequest(selectedProviders: LogisticsProvider[], po_number?: string) {
    setLoading(true)
    try {
      const response = await api_fetch(`/deduction/${deduction.id}/request-logistics-bol-pod`, {
        method: "POST",
        body: {
          provider_emails: selectedProviders.map(p => p.email),
          ...(po_number && po_number.trim().length > 0 ? { po_number } : {}),
        },
      })

      if (!response.ok) {
        throw new Error(response.error?.message || "Failed to request BOL and POD from logistics providers")
      }

      const data = response.value.data as { message: string; providers_contacted: number }
      toast({
        title: "Request sent",
        description: data.message,
      })
    } catch (error) {
      console.error("Error requesting documents from logistics providers:", error)
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to request BOL and POD from logistics providers",
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }

  // submitStord removed; use handleStordBolRequest instead

  function handleLogisticsProvidersSaved(providers: LogisticsProvider[]) {
    // Refresh the page to get updated logistics providers
    RefreshFlag.set(f => !f)
    setShowLogisticsDialog(false)
    // Merge new providers into current selection if possible
    if (providers && providers.length > 0) {
      // Auto-select the last added provider(s)
      const emails = new Set(providers.map(p => p.email))
      const newlyAdded = providers.filter(p => emails.has(p.email))
      setSelectedProviders(prev => Array.from(new Set([...prev, ...newlyAdded])))
    }
  }

  async function handleProviderSelection(selectedProviders: LogisticsProvider[]) {
    setShowProviderSelection(false)
    await sendLogisticsRequest(selectedProviders, poInput)
  }

  return (
    <div class="flex gap-2 mb-4">
      {deduction.has_stord_config && (
        <Button
          onClick={() => {
            // A PO on the deduction is the Stord order number: search with it at once. The
            // dialog only exists to collect a PO when there is none.
            const po = stord_po_number_to_search(deduction.po_number)
            if (po) {
              void handleStordBolRequest(po)
              return
            }
            setPoInput("")
            setRequestKind("stord")
          }}
          disabled={loading}
          variant="outline"
          // @ts-ignore
          size="sm"
          class="flex items-center gap-2">
          <Package className="h-4 w-4" />
          Request BOL from Stord
        </Button>
      )}
      {deduction.kind !== "repayment" && (
        <Button
          onClick={() => {
            if (logistics_providers.length === 0) {
              setShowLogisticsDialog(true)
              return
            }
            setPoInput(deduction.po_number ?? "")
            // Check if any portal provider is recommended - if so, don't auto-select email providers
            const portalProviders = logistics_providers.filter((p: LogisticsProvider) => p.access_method === "portal")
            const recommendedPortal = portalProviders.some((p: LogisticsProvider) => p.is_recommended)
            
            if (recommendedPortal) {
              // Portal is recommended - don't auto-select any email providers
              setSelectedProviders([])
            } else {
              // Auto-select recommended email providers, or all email providers if none recommended
              const emailProviders = logistics_providers.filter((p: LogisticsProvider) => p.access_method !== "portal")
              const recommendedEmail = emailProviders.filter((p: LogisticsProvider) => p.is_recommended)
              if (recommendedEmail.length > 0) {
                setSelectedProviders(recommendedEmail)
              } else if (emailProviders.length <= 5) {
                setSelectedProviders(emailProviders)
              } else {
                setSelectedProviders([])
              }
            }
            setRequestKind("logistics")
          }}
          disabled={loading}
          variant="outline"
          // @ts-ignore
          size="sm"
          class="flex items-center gap-2">
          <Truck className="h-4 w-4" />
          Request BOL and POD
        </Button>
      )}

      <LogisticsProviderForm
        isOpen={showLogisticsDialog}
        onClose={() => setShowLogisticsDialog(false)}
        onSave={handleLogisticsProvidersSaved}
        existingProviders={logistics_providers}
        title="Set Up Logistics Providers"
        description="Add logistics providers to receive BOL and POD requests for this deduction."
      />

      {/* Selection modal removed in favor of unified modal */}

      <Dialog open={requestKind !== null} onOpenChange={open => !open && setRequestKind(null)}>
        <DialogContent class="max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>{requestKind === "stord" ? "Request BOL from Stord" : "Request BOL and POD"}</DialogTitle>
            <DialogDescription>Enter a PO number to include with your request. This is optional.</DialogDescription>
          </DialogHeader>

          <div class="flex-1 overflow-y-auto min-h-0 pr-2">
            {shouldShowWarning() && (
              <div class="rounded-md border border-amber-300 bg-amber-50 text-amber-900 p-3 text-sm mb-3">
                Many 3PLs require a PO number, but you can continue without one if your 3PL will be able to find the
                order via the invoice number.
                <div class="mt-2 flex items-center gap-2">
                  <input
                    id="dont-remind"
                    type="checkbox"
                    onChange={e => setDontRemind((e.target as HTMLInputElement).checked)}
                  />
                  <Label htmlFor="dont-remind">Don’t remind me again</Label>
                </div>
              </div>
            )}

            <div class="space-y-2">
              <Label htmlFor="po-number">PO Number (optional)</Label>
              <Input
                id="po-number"
                value={poInput}
                onChange={e => setPoInput((e.currentTarget as HTMLInputElement).value)}
                placeholder="Enter PO number"
              />
            </div>

            {requestKind === "logistics" && (
              <div class="mt-4">
                {/* Guidance URL */}
                {guidance_url && (
                  <div class="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                    <div class="flex items-center gap-2">
                      <ExternalLink className="h-4 w-4 text-blue-600 shrink-0" />
                      <span class="text-sm text-blue-900">
                        Need help choosing a provider?{" "}
                        <a
                          href={guidance_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          class="text-blue-600 hover:underline font-medium"
                        >
                          View reference guide
                        </a>
                      </span>
                    </div>
                  </div>
                )}

                {/* Portal Providers - Reference Only */}
                {logistics_providers.filter((p: LogisticsProvider) => p.access_method === "portal").length > 0 && (
                  <div class="mb-4">
                    <Label class="text-gray-700">Portal Access (Reference Only)</Label>
                    <p class="text-xs text-gray-500 mb-2">Use these portals to look up BOL/POD directly</p>
                    <div class="border rounded-md p-2 space-y-2 bg-gray-50">
                      {logistics_providers
                        .filter((p: LogisticsProvider) => p.access_method === "portal")
                        .map((p: LogisticsProvider, index: number) => (
                          <div 
                            key={`portal-${p.id || p.email}-${index}`} 
                            class={`p-2 rounded-md bg-white border ${p.is_recommended ? "border-green-300 bg-green-50" : "border-gray-200"}`}
                          >
                            <div class="flex items-center gap-2">
                              <span class="font-medium text-gray-900">{p.name}</span>
                              {p.is_recommended && (
                                <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  <Star className="h-3 w-3" />
                                  Recommended
                                </span>
                              )}
                            </div>
                            {p.is_recommended && p.recommendation_reason && (
                              <div class="text-green-700 text-xs mt-1 font-medium">{p.recommendation_reason}</div>
                            )}
                            {p.portal_url && (
                              <div class="text-sm mt-1">
                                <a 
                                  href={p.portal_url.startsWith("http") ? p.portal_url : `https://${p.portal_url}`} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  class="text-blue-600 hover:underline"
                                >
                                  {p.portal_url}
                                </a>
                              </div>
                            )}
                            {console.log("user.role:", (user as any)?.role, "portal credentials:", p.portal_username, p.portal_password)}
                            {(user as any)?.role === "system_admin" && (p.portal_username || p.portal_password) && (
                              <div class="mt-2 p-2 bg-gray-100 rounded text-xs space-y-1">
                                {p.portal_username && (
                                  <div class="flex items-center gap-2">
                                    <span class="text-gray-500">Username:</span>
                                    <code class="bg-white px-1 rounded select-all">{p.portal_username}</code>
                                  </div>
                                )}
                                {p.portal_password && (
                                  <div class="flex items-center gap-2">
                                    <span class="text-gray-500">Password:</span>
                                    <code class="bg-white px-1 rounded select-all">{p.portal_password}</code>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                    </div>
                  </div>
                )}

                {/* Email Providers - Selectable */}
                <div class="mb-2 flex items-center justify-between">
                  <Label>Email Request</Label>
                  <Button
                    variant="outline" // @ts-ignore size="sm"
                    onClick={() => setShowInlineProviderCreate(true)}>
                    Add provider
                  </Button>
                </div>
                {logistics_providers.filter((p: LogisticsProvider) => p.access_method !== "portal").length === 0 && (
                  <div class="text-sm text-gray-600 mb-2">No email providers configured. Add one below.</div>
                )}
                <div class="max-h-56 overflow-auto border rounded-md p-2 space-y-3">
                  {logistics_providers
                    .filter((p: LogisticsProvider) => p.access_method !== "portal")
                    .map((p: LogisticsProvider, index: number) => {
                      const checked = selectedProviders.some((sp: LogisticsProvider) => sp.email === p.email)
                      return (
                        <div 
                          key={`email-${p.id || p.email}-${index}`} 
                          class={`flex items-start gap-2 p-2 rounded-md ${p.is_recommended ? "bg-green-50 border border-green-200" : ""}`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={e => {
                              const isChecked = (e.target as HTMLInputElement).checked
                              setSelectedProviders((prev: LogisticsProvider[]) =>
                                isChecked ? [...prev, p] : prev.filter((sp: LogisticsProvider) => sp.email !== p.email)
                              )
                            }}
                            class="mt-1"
                          />
                          <div class="flex-1 text-sm">
                            <div class="flex items-center gap-2">
                              <span class="font-medium text-gray-900">{p.name || p.email}</span>
                              {p.is_recommended && (
                                <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  <Star className="h-3 w-3" />
                                  Recommended
                                </span>
                              )}
                            </div>
                            {p.is_recommended && p.recommendation_reason && (
                              <div class="text-green-700 text-xs mt-1 font-medium">{p.recommendation_reason}</div>
                            )}
                            {p.name && <div class="text-gray-600">{p.email}</div>}
                            {p.contact_name && <div class="text-gray-500 mt-1">Contact: {p.contact_name}</div>}
                            {p.details && <div class="text-gray-500 mt-1">Details / Order Types: {p.details}</div>}
                            {p.cc_emails && p.cc_emails.length > 0 && (
                              <div class="text-gray-500 mt-1">CC: {p.cc_emails.join(", ")}</div>
                            )}
                          </div>
                        </div>
                      )
                    })}
                </div>

                {showInlineProviderCreate && (
                  <div class="mt-3">
                    <LogisticsProviderForm
                      isOpen={true}
                      onClose={() => setShowInlineProviderCreate(false)}
                      onSave={handleLogisticsProvidersSaved}
                      existingProviders={logistics_providers}
                      title="Add Logistics Provider"
                      description="Create a new logistics provider to send requests to."
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          <div class="mt-4 flex justify-end gap-2 shrink-0">
            <Button variant="outline" onClick={() => setRequestKind(null)}>
              Cancel
            </Button>
            <Button
              onClick={async () => {
                const po = poInput?.trim()
                const usePo = po && po.length > 0 ? po : undefined
                if (requestKind === "stord") {
                  await handleStordBolRequest(usePo)
                  setRequestKind(null)
                } else if (requestKind === "logistics") {
                  if (selectedProviders.length === 0) return
                  await sendLogisticsRequest(selectedProviders, usePo)
                  setRequestKind(null)
                }
              }}
              disabled={
                requestKind === "logistics" && 
                logistics_providers.filter((p: LogisticsProvider) => p.access_method !== "portal").length > 0 && 
                selectedProviders.length === 0
              }>
              Request
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
